import type { Diagram } from "../shared/companion";

export interface StateResponse {
  user: { id: string; email: string; name: string | null; picture: string | null; challenge: string | null };
  messages: { id: string; role: "user" | "assistant"; content: string; diagram: Diagram | null; cites: string[] | null }[];
}

/** Fetch the signed-in user + persisted chat. Returns null if unauthenticated. */
export async function fetchState(): Promise<StateResponse | null> {
  const res = await fetch("/api/state", { headers: { Accept: "application/json" } });
  if (res.status === 401) return null;
  if (!res.ok) throw new Error("state " + res.status);
  return (await res.json()) as StateResponse;
}

export async function saveChallenge(challenge: string): Promise<void> {
  await fetch("/api/challenge", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ challenge }),
  });
}

export async function saveNoteApi(messageId: string): Promise<boolean> {
  const res = await fetch("/api/notes", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ messageId }),
  });
  return res.ok;
}

export interface ChatDone {
  messageId: string;
  text: string;
  diagram: Diagram | null;
  cites: string[];
}

/**
 * Stream a Companion reply. Calls `onDelta` with visible prose as it arrives
 * and resolves with the final parsed reply once the stream completes.
 */
export async function streamChat(
  message: string,
  challengeMode: boolean,
  onDelta: (text: string) => void
): Promise<ChatDone> {
  const res = await fetch("/api/chat", {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ message, challengeMode }),
  });
  if (res.status === 401) throw new Error("unauthorized");
  if (!res.ok || !res.body) throw new Error("chat " + res.status);

  const reader = res.body.getReader();
  const decoder = new TextDecoder();
  let buf = "";
  let done: ChatDone | null = null;

  const handleLine = (line: string) => {
    const trimmed = line.trim();
    if (!trimmed) return;
    let evt: { type: string; text?: string; messageId?: string; diagram?: Diagram | null; cites?: string[] };
    try {
      evt = JSON.parse(trimmed);
    } catch {
      return;
    }
    if (evt.type === "delta" && evt.text) onDelta(evt.text);
    else if (evt.type === "done") {
      done = {
        messageId: evt.messageId || "",
        text: evt.text || "",
        diagram: evt.diagram ?? null,
        cites: evt.cites || [],
      };
    }
  };

  while (true) {
    const { value, done: streamDone } = await reader.read();
    if (streamDone) break;
    buf += decoder.decode(value, { stream: true });
    const lines = buf.split("\n");
    buf = lines.pop() ?? "";
    for (const line of lines) handleLine(line);
  }
  if (buf) handleLine(buf);

  if (!done) throw new Error("no_done_event");
  return done;
}

export function clearChatApi(): Promise<{ ok: boolean }> {
  return postJson("/api/chat/clear", {});
}
export function deleteAccountApi(): Promise<{ ok: boolean }> {
  return postJson("/api/me/delete", {});
}
export const EXPORT_URL = "/api/me/export";

// ---------------------------------------------------------------- shared helpers
async function getJson<T>(url: string): Promise<T> {
  const res = await fetch(url, { headers: { Accept: "application/json" } });
  if (res.status === 401) throw new Error("unauthorized");
  if (!res.ok) throw new Error(url + " " + res.status);
  return (await res.json()) as T;
}
async function postJson<T>(url: string, body: unknown): Promise<T> {
  const res = await fetch(url, { method: "POST", headers: { "Content-Type": "application/json" }, body: JSON.stringify(body) });
  if (res.status === 401) throw new Error("unauthorized");
  if (!res.ok) throw new Error(url + " " + res.status);
  return (await res.json()) as T;
}

// ---------------------------------------------------------------- reflections
export interface ReflectionEntry {
  body: string;
  updatedAt: number;
}
export function fetchReflections(): Promise<{ reflections: Record<string, ReflectionEntry> }> {
  return getJson("/api/reflections");
}
export function saveReflectionApi(promptKey: string, body: string): Promise<{ ok: boolean }> {
  return postJson("/api/reflections", { promptKey, body });
}

// ---------------------------------------------------------------- diagnostics
export interface DiagResultEntry {
  outcomeKey: string;
  scores: Record<string, number>;
  createdAt: number;
}
export function fetchDiagnostics(): Promise<{ results: Record<string, DiagResultEntry> }> {
  return getJson("/api/diagnostics");
}
export function saveDiagnosticApi(diagnosticId: string, outcomeKey: string, scores: Record<string, number>): Promise<{ ok: boolean }> {
  return postJson("/api/diagnostics", { diagnosticId, outcomeKey, scores });
}

// ---------------------------------------------------------------- suggestions
export interface Suggestion {
  id: string;
  title: string;
  body: string;
  status: string;
  votes: number;
  voted: boolean;
  mine: boolean;
  author: string;
  createdAt: number;
  canSetStatus: boolean;
}
export function fetchSuggestions(): Promise<{ suggestions: Suggestion[]; isAdmin: boolean }> {
  return getJson("/api/suggestions");
}
export function createSuggestionApi(title: string, body: string, anonymous: boolean): Promise<{ ok: boolean; id: string }> {
  return postJson("/api/suggestions", { title, body, anonymous });
}
export function voteSuggestionApi(id: string): Promise<{ ok: boolean; voted: boolean }> {
  return postJson(`/api/suggestions/${id}/vote`, {});
}
export function setSuggestionStatusApi(id: string, status: string): Promise<{ ok: boolean; status: string }> {
  return postJson(`/api/suggestions/${id}/status`, { status });
}
